import {
  Toolbar,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  useTheme,
  CardActionArea,
  Grow,
  Paper,
  useMediaQuery,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
} from "recharts";
import { useNavigate } from "react-router-dom";
import useGetCount from "../hooks/useGetCount";
import { API_ENDPOINT } from "../utils/constants";
import useCompanySettings from "../hooks/useCompanySettings";
import { useContext, useEffect } from "react";
import { UserContext } from "../context/UserProvider";
import FileCopyIcon from "@mui/icons-material/FileCopy"; // Added icon for copy
import { toast } from "react-toastify"; // Added for notification
import MuPb from "../widgets/MuPb";

const chartData = [
  { name: "Jan", value: 4000 },
  { name: "Feb", value: 3000 },
  { name: "Mar", value: 5000 },
  { name: "Apr", value: 2780 },
  { name: "May", value: 1890 },
  { name: "Jun", value: 2390 },
];

export const DashboardLayout = () => {
  const { data, isLoading } = useCompanySettings();
  const { user, loading: userLoading } = useContext(UserContext);
  const theme = useTheme();
  const navigate = useNavigate();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  // API calls with consistent loading states
  const { data: empCount, isLoading: empLoading } = useGetCount(
    `${API_ENDPOINT}user/members-count?type=Employee`
  );
  const { data: securityCount, isLoading: securityLoading } = useGetCount(
    `${API_ENDPOINT}user/members-count?type=Gatekeeper`
  );
  const { data: meetingCount, isLoading: meetingLoading } = useGetCount(
    `${API_ENDPOINT}user/meetings-count-org`
  );
  const { data: meetingCountP, isLoading: pendingLoading } = useGetCount(
    `${API_ENDPOINT}user/meetings-count-org?status=pending`
  );

  useEffect(() => {
    if (!isLoading && !userLoading) {
      const settings = data?.settings || {};
      if (!data || !settings.srLogo || !settings.bnLogo) {
        navigate("/home/account-setup");
      }
    }
  }, [data, isLoading, userLoading, navigate]);

  // Only show stats when all data is loaded
  const isDataLoading =
    empLoading || securityLoading || meetingLoading || pendingLoading;

  const statCards = [
    {
      title: "Employees",
      subheader: "Total employees",
      value: isDataLoading ? "..." : empCount?.count ?? 0,
      color: theme.palette.primary.main,
      link: "/home/staff",
    },
    {
      title: "Gatekeepers",
      subheader: "Currently online",
      value: isDataLoading ? "..." : securityCount?.count ?? 0,
      color: theme.palette.secondary.main,
      link: "/home/gatekeeper",
    },
    {
      title: "Total Requests",
      subheader: "Current year",
      value: isDataLoading ? "..." : meetingCount?.count ?? 0,
      color: theme.palette.success.main,
      link: "/home/visitor-requests",
    },
    {
      title: "Pending Requests",
      subheader: "Awaiting approval",
      value: isDataLoading ? "..." : meetingCountP?.count ?? 0,
      color: theme.palette.warning.main,
      link: "/home/pending-requests",
    },
  ];

  const handleCardClick = (link) => {
    navigate(link);
  };

  const handleCopyLink = () => {
    if (user?.visitorRegLink) {
      navigator.clipboard
        .writeText(user.visitorRegLink)
        .then(() => {
          toast.success("Registration link copied to clipboard!");
        })
        .catch((err) => {
          toast.error("Failed to copy link");
          console.error("Copy failed:", err);
        });
    }
  };

  if (isLoading || userLoading) {
    return <MuPb />;
  }

  return (
    <Box sx={{ p: isSmallScreen ? 1 : 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <a
          href={user?.visitorRegLink}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Typography gutterBottom variant="h6" sx={{ mr: 1 }}>
            Visitor Registration Link
          </Typography>
        </a>
        <Tooltip title="Copy to clipboard">
          <IconButton
            onClick={handleCopyLink}
            size="small"
            sx={{ color: theme.palette.text.secondary }}
          >
            <FileCopyIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Grid container spacing={3}>
        {/* Stats Cards */}
        {statCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Grow in={!isDataLoading} timeout={(index + 1) * 300}>
              <Card
                sx={{
                  height: "100%",
                  transition: "transform 0.3s, box-shadow 0.3s",
                  "&:hover": {
                    transform: "translateY(-5px)",
                    boxShadow: theme.shadows[8],
                  },
                }}
                elevation={4}
              >
                <CardActionArea onClick={() => handleCardClick(card.link)}>
                  <CardHeader
                    title={card.title}
                    subheader={card.subheader}
                    titleTypographyProps={{
                      variant: "h6",
                      color: card.color,
                    }}
                    subheaderTypographyProps={{ variant: "subtitle2" }}
                  />
                  <CardContent>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: card.color,
                      }}
                    >
                      {card.value}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grow>
          </Grid>
        ))}

        {/* Chart */}
        <Grid item xs={12}>
          <Grow in={!isDataLoading} timeout={1500}>
            <Paper elevation={4}>
              <Card
                sx={{
                  transition: "box-shadow 0.3s",
                  "&:hover": {
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                <CardHeader
                  title="Activity Overview"
                  titleTypographyProps={{
                    variant: "h5",
                    color: "text.primary",
                  }}
                />
                <CardContent sx={{ height: 400 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={theme.palette.divider}
                      />
                      <XAxis
                        dataKey="name"
                        tick={{ fill: theme.palette.text.secondary }}
                      />
                      <YAxis tick={{ fill: theme.palette.text.secondary }} />
                      <ChartTooltip
                        contentStyle={{
                          backgroundColor: theme.palette.background.paper,
                          borderColor: theme.palette.divider,
                          borderRadius: theme.shape.borderRadius,
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="value"
                        stroke={theme.palette.primary.main}
                        fill={theme.palette.primary.light}
                        fillOpacity={0.8}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </Paper>
          </Grow>
        </Grid>
      </Grid>
    </Box>
  );
};
